/*
 * COPYRIGHT Transtron Inc 2022
 *
 * 機能名：コモン系のAPI
 *
 * 作成者：2021/11/19 ノア）黄
 * 最終修正者：2021/11/19 ノア）黄
 *
 */
import { Vue } from 'vue-property-decorator'

import { apiClient } from '@/store/apis/apiClient'
import config from '@/config/config'
import {
  LoginParam,
  MFAVerifyParam,
  MFASwitchParam,
  MFAInfo,
  UpdatePasswordParam,
  LoginResponse,
  CommonResponse,
  PhoneCC,
  Bookmarks,
  BookmarksParam,
  ImagePathData
} from '@/models'
import { MqttParams } from '@/utils/mqttUtils'

/**
 * ログインする
 *
 * @async
 * @param {LoginParam} loginParam ログインのパラメータ
 * @returns {Promise<LoginResponse>} アクセストークン
 */
export async function postLogin(loginParam: LoginParam): Promise<LoginResponse> {
  const res = await apiClient.post(config.api.login, loginParam)
  Vue.$log.debug('apis > postLogin: res=', res)
  if (!res || !res.data) {
    throw new Error('apis > postLogin: res is null')
  }

  return (res.data as CommonResponse).data as LoginResponse
}

/**
 * MFAパスワードの検証
 *
 * @async
 * @param {MFAVerifyParam} mfaVerifyParam MFAインのパラメータ
 * @returns {Promise<LoginResponse>} アクセストークン
 */
export async function postMFAVerify(mfaVerifyParam: MFAVerifyParam): Promise<LoginResponse> {
  const res = await apiClient.post(config.api.verification, mfaVerifyParam)
  Vue.$log.debug('apis > postMFAVerify: res=', res)
  if (!res || !res.data) {
    throw new Error('apis > postMFAVerify: res is null')
  }

  return (res.data as CommonResponse).data as LoginResponse
}

/**
 * ログアウトする
 *
 * @async
 * @returns {Promise<void>}
 */
export async function postLogout(): Promise<void> {
  const res = await apiClient.post(config.api.logout)
  Vue.$log.debug('apis > postLogout: res=', res)
}

/**
 * パスワードを変更する
 *
 * @async
 * @param {UpdatePasswordParam} updatePasswordParam パスワード変更のパラメータ
 * @returns {Promise<void>}
 */
export async function postUpdatePassword(updatePasswordParam: UpdatePasswordParam): Promise<void> {
  const res = await apiClient.post(config.api.updatePassword, updatePasswordParam)
  Vue.$log.debug('apis > postUpdatePassword: res=', res)
}

/**
 * MFA認証スイッチを変更する
 *
 * @async
 * @param {MFASwitchParam} mfaSwitchParam パスワード変更のパラメータ
 * @returns {Promise<void>}
 */
export async function postMFASwitch(mfaSwitchParam: MFASwitchParam): Promise<void> {
  const res = await apiClient.post(config.api.mfaSwitch, mfaSwitchParam)
  Vue.$log.debug('apis > postMFASwitch: res=', res)
}

/**
 * MFA情報を取得する。
 *
 * @async
 * @returns {Promise<MFAInfo>} MFA情報
 */
export async function getMFAInfo(): Promise<MFAInfo> {
  const res = await apiClient.get(config.api.getMFAInfo)
  Vue.$log.debug('apis > getMFAInfo: res=', res)
  if (!res || !res.data) {
    throw new Error('apis > getMFAInfo: res is null')
  }
  return res.data.data as MFAInfo
}

/**
 * CC(電話)を取得する。
 *
 * @async
 * @returns {Promise<void>}
 */
export async function getCaller(): Promise<PhoneCC> {
  const res = await apiClient.get(config.api.getCallerData)
  Vue.$log.debug('apis > getCaller: res=', res)
  if (!res || !res.data) {
    throw new Error('apis > getCaller: res is null')
  }
  return res.data.data as PhoneCC
}

/**
 * CC(電話)を更新する。
 *
 * @async
 * @param {PhoneCC} updateCallerParam CC(電話)のパラメータ
 * @returns {Promise<void>}
 */
export async function updateCaller(updateCallerParam: PhoneCC): Promise<void> {
  const res = await apiClient.post(config.api.updateCallerData, updateCallerParam)
  Vue.$log.debug('apis > updateCaller: res=', res)
}

/**
 * お気に入りを取得する。
 *
 * @async
 * @returns {Promise<void>}
 */
export async function getBookmarks(): Promise<Bookmarks> {
  const res = await apiClient.get(config.api.getFavMenuData)
  Vue.$log.debug('apis > getCaller: res=', res)
  if (!res || !res.data) {
    throw new Error('apis > getCaller: res is null')
  }
  return res.data.data as Bookmarks
}

/**
 * お気に入りを更新する。
 *
 * @async
 * @param {BookmarksParam} updateBookmarksParam お気に入りのパラメータ
 * @returns {Promise<void>}
 */
export async function updateBookmarks(updateBookmarksParam: BookmarksParam): Promise<void> {
  const res = await apiClient.post(config.api.updateFavMenuData, updateBookmarksParam)
  Vue.$log.debug('apis > updateCaller: res=', res)
}

/**
 * 管理場所画像アップロード
 *
 * @async
 * @param {FormData} uploadImgParam アップロード画像
 * @returns {Promise<ImagePathData>} 画像パス
 */
export async function uploadPlaceImg(uploadImgParam: FormData): Promise<ImagePathData> {
  const res = await apiClient.post(config.api.uploadPlaceImgData, uploadImgParam)
  if (!res || !res.data) {
    throw new Error('apis > uploadPlaceImg: res is null')
  }
  return res.data.data as ImagePathData
}

/**
 * 管理場所詳細画像アップロード
 *
 * @async
 * @param {FormData} uploadImgParam アップロード画像
 * @returns {Promise<ImagePathData>} 画像パス
 */
export async function uploadPlaceDtlImg(uploadImgParam: FormData): Promise<ImagePathData> {
  const res = await apiClient.post(config.api.uploadPlaceDtlImgData, uploadImgParam)
  if (!res || !res.data) {
    throw new Error('apis > uploadPlaceDtlImg: res is null')
  }
  return res.data.data as ImagePathData
}

/**
 * MQTTコンフィグを取得する。
 *
 * @async
 * @returns {Promise<void>}
 */
export async function getMQTTConfig(): Promise<MqttParams> {
  const res = await apiClient.get(config.api.getMQTTConfig)
  Vue.$log.debug('apis > getMQTTConfig: res=', res)
  if (!res || !res.data) {
    throw new Error('apis > getMQTTConfig: res is null')
  }
  return res.data.data as MqttParams
}

/**
 * トークン時間更新する。
 *
 * @async
 * @returns {Promise<void>}
 */
export async function refreshToken(): Promise<void> {
  const res = await apiClient.post(config.api.refreshToken)
  Vue.$log.debug('apis > refreshToken: res=', res)
}
